// src/pages/Numsense.js
import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useAuth } from '../context/AuthContext';
import Leaderboard from '../opencomponents/Leaderboard';
import { FaTrophy } from 'react-icons/fa';
import { apiClient } from '../api/apiClient';
import PageShell from '../components/PageShell';
import { PrimaryButton, GhostButton, Input, Muted } from '../components/ui';
import { color, font } from '../theme/tokens';

// =============================================================================
// PART 1: ANIMATIONS
// =============================================================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const dotAppear = keyframes`
  from { opacity: 0; transform: scale(0); }
  to { opacity: 1; transform: scale(1); }
`;

// =============================================================================
// PART 2: STYLED COMPONENTS
// =============================================================================

const GameWrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    width: 100%;
    min-height: clamp(560px, calc(100vh - 160px), 760px);
    background: ${color.surface};
    border: 1px solid ${color.line};
    color: ${color.text};
    font-family: ${font.body};
    transition: box-shadow 0.4s ease, border-color 0.4s ease;

    /* corner ticks */
    &::before, &::after {
        content: '';
        position: absolute;
        width: 14px;
        height: 14px;
        pointer-events: none;
        z-index: 3;
    }
    &::before { left: -1px; top: -1px; border-left: 1px solid ${color.blood}; border-top: 1px solid ${color.blood}; }
    &::after { right: -1px; bottom: -1px; border-right: 1px solid ${color.blood}; border-bottom: 1px solid ${color.blood}; }

    /* Feedback States */
    ${({ $feedback }) => $feedback === 'correct' && css`
        border-color: ${color.line2};
        box-shadow: inset 0 0 0 1px ${color.text2};
    `}
    ${({ $feedback }) => $feedback === 'incorrect' && css`
        border-color: ${color.bloodHi};
        box-shadow: inset 0 0 60px rgba(200, 50, 62, 0.22);
    `}
`;

const GameHeader = styled.header`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding: 14px 24px;
    border-bottom: 1px solid ${color.line};
    flex-shrink: 0;
`;

const GameStats = styled.div`
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 18px;
    font-size: 13px;
    letter-spacing: 0.16em;
    color: ${color.text3};
    b { font-family: ${font.mono}; font-weight: 500; letter-spacing: 0.04em; color: ${color.text}; margin-left: 6px; }
    .mode { font-family: ${font.display}; font-size: 14px; letter-spacing: 0.2em; color: ${color.bloodHi}; }
`;

const FeedbackText = styled.span`
    min-width: 3em;
    font-family: ${font.display};
    font-size: 14px;
    letter-spacing: 0.3em;
    color: ${({ $feedback }) => ($feedback === 'incorrect' ? color.bloodHi : color.textBright)};
`;

const GameCanvas = styled.div`
    flex-grow: 1;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const Dot = styled.div.attrs(props => ({
    style: {
        top: `${props.y}%`,
        left: `${props.x}%`,
        width: `${props.size}px`,
        height: `${props.size}px`,
    }
}))`
    position: absolute;
    background-color: ${color.textBright};
    border-radius: 50%;
    animation: ${dotAppear} 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
`;

const CenteredContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 56px 24px;
    box-sizing: border-box;
    animation: ${fadeIn} 0.45s ease-out;
    position: relative;
    z-index: 2;

    @media (max-width: 640px) { padding: 72px 16px 40px; }
`;

const Title = styled.h2`
    margin: 0 0 10px;
    font-family: ${font.display};
    font-weight: 800;
    font-size: ${({ $small }) => ($small ? '30px' : '40px')};
    letter-spacing: 0.2em;
    text-align: center;
    color: ${color.textBright};
    text-shadow: 0 0 24px rgba(200, 50, 62, 0.35);
`;

const Subtitle = styled.p`
    margin: 0 0 40px;
    font-size: 14px;
    letter-spacing: 0.24em;
    text-align: center;
    color: ${color.text3};
`;

const OptionsWrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    width: 100%;
    max-width: 560px;
`;

const ModeButton = styled(GhostButton)`
    min-height: 64px;
    flex-direction: column;
    gap: 4px;
    font-size: 17px;
    color: ${color.text};
    .en { font-family: ${font.mono}; font-size: 10px; letter-spacing: 0.3em; color: ${color.text3}; }
    &:hover:not(:disabled) { background: ${color.surface2}; }
`;

const AnswerButton = styled(GhostButton)`
    min-height: 72px;
    font-family: ${font.mono};
    font-size: 26px;
    letter-spacing: 0.04em;
    color: ${color.textBright};
    &:hover:not(:disabled) { background: ${color.surface2}; }
`;

const AbortButton = styled(GhostButton)`
    min-height: 36px;
    padding: 0 16px;
    font-size: 13px;
    border-color: rgba(224, 70, 79, 0.5);
    color: ${color.bloodHi};
    &:hover:not(:disabled) { border-color: ${color.bloodHi}; color: ${color.textBright}; }
`;

const LeaderboardButton = styled(GhostButton)`
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 20;
    min-height: 40px;
    padding: 0 16px;
    gap: 10px;
    font-size: 13px;
    letter-spacing: 0.2em;
    background: ${color.void};
    svg { color: ${color.bloodHi}; font-size: 13px; }

    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
`;

// --- Custom Mode Form ---

const CustomForm = styled.form`
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 14px 20px;
    align-items: center;
    width: 100%;
    max-width: 480px;
    box-sizing: border-box;
    margin-top: 40px;
    padding: 28px 28px 24px;
    border: 1px solid ${color.line};
    background: rgba(7, 2, 5, 0.5);

    @media (max-width: 520px) {
        grid-template-columns: 1fr;
        label { justify-self: start; }
    }
`;

const FormLabel = styled.label`
    justify-self: end;
    font-size: 13px;
    letter-spacing: 0.18em;
    color: ${color.text2};
`;

const FormInput = styled(Input)`
    height: 42px;
    font-family: ${font.mono};
`;

const FormHeader = styled.h3`
    grid-column: 1 / -1;
    margin: 0 0 6px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${color.line};
    font-family: ${font.display};
    font-weight: 700;
    font-size: 16px;
    letter-spacing: 0.3em;
    text-align: center;
    color: ${color.text};
`;

// --- Stats Display ---

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    width: 100%;
    max-width: 560px;
    margin-bottom: 28px;
`;

const StatItem = styled.div`
    padding: 22px;
    text-align: center;
    border: 1px solid ${({ $accent }) => ($accent ? 'rgba(224, 70, 79, 0.5)' : color.line)};
    background: ${({ $accent }) => ($accent ? 'rgba(200, 50, 62, 0.08)' : 'rgba(7, 2, 5, 0.5)')};
    ${({ $wide }) => $wide && 'grid-column: 1 / -1;'}
`;

const StatLabel = styled.div`
    margin-bottom: 8px;
    font-size: 13px;
    letter-spacing: 0.24em;
    color: ${color.text3};
`;

const StatValue = styled.div`
    font-family: ${font.mono};
    font-size: 26px;
    font-weight: 500;
    color: ${color.text};
`;

const FinalScore = styled(StatValue)`
    font-size: 48px;
    color: ${color.textBright};
    text-shadow: 0 0 24px rgba(200, 50, 62, 0.4);
`;

const Note = styled.p`
    margin: 0 0 8px;
    font-size: 14px;
    letter-spacing: 0.12em;
    color: ${({ $dim }) => ($dim ? color.text3 : color.text2)};
`;

// 显示用的模式名称（提交给服务器的 name 保持原值）
const MODE_LABELS = {
    BEGINNER: '初级',
    INTERMEDIATE: '中级',
    ADVANCED: '高级',
    HELL: '地狱',
    'CUSTOM PROTOCOL': '自定义',
};
const modeLabel = (name) => MODE_LABELS[name] || name;


// =============================================================================
// PART 3: GAME LOGIC & COMPONENT
// =============================================================================

// --- 游戏模式定义 ---
const GAME_MODES = {
    beginner: { name: 'BEGINNER', x1: 4, x2: 8, n: 0.6, P: 10, scoreMultiplier: 100 },
    intermediate: { name: 'INTERMEDIATE', x1: 6, x2: 10, n: 0.5, P: 10, scoreMultiplier: 150 },
    advanced: { name: 'ADVANCED', x1: 6, x2: 14, n: 0.4, P: 10, scoreMultiplier: 250 },
    hell: { name: 'HELL', x1: 6, x2: 20, n: 0.3, P: 10, scoreMultiplier: 500 },
};

// --- 辅助函数 ---
const getRandomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const generateOptions = (correctAnswer, min, max) => {
    const options = new Set([correctAnswer]);
    while (options.size < 4) {
        const option = getRandomInt(Math.max(min, correctAnswer - 5), Math.min(max, correctAnswer + 5));
        if (option > 0) {
            options.add(option);
        }
    }
    return Array.from(options).sort(() => Math.random() - 0.5);
};

function NumsenseGame({ user }) {
    // 游戏核心状态
    const [gameState, setGameState] = useState('MODE_SELECTION'); // MODE_SELECTION, SHOWING_DOTS, ASKING_QUESTION, GAME_OVER
    const [settings, setSettings] = useState(null);
    
    // 当前回合状态
    const [dots, setDots] = useState([]);
    const [correctAnswer, setCorrectAnswer] = useState(0);
    const [options, setOptions] = useState([]);
    const [feedback, setFeedback] = useState(null);

    // 游戏整体统计
    const [questionNumber, setQuestionNumber] = useState(0);
    const [score, setScore] = useState(0);
    const [finalScore, setFinalScore] = useState(0);
    const [isScoreSubmitted, setIsScoreSubmitted] = useState(false);
    // const [history, setHistory] = useState([]); // Removed unused history
 
    // 排行榜
    const [showLeaderboard, setShowLeaderboard] = useState(false);

    // 自定义模式表单
    const [customSettings, setCustomSettings] = useState({ x1: 6, x2: 20, n: 0.3, P: 10 });

    const calculateFinalScore = useCallback((correctAnswers, gameSettings) => {
        if (!gameSettings) return 0;
        let multiplier;
        if (gameSettings.scoreMultiplier) {
            multiplier = gameSettings.scoreMultiplier;
        } else {
            const avgDots = (gameSettings.x1 + gameSettings.x2) / 2;
            const timeFactor = 1 / gameSettings.n;
            multiplier = Math.round(avgDots * timeFactor * 10);
        }
        return correctAnswers * multiplier;
    }, []);

    useEffect(() => {
        if (gameState === 'GAME_OVER' && user && !isScoreSubmitted) {
            const calculatedScore = calculateFinalScore(score, settings);
            setFinalScore(calculatedScore);
 
            const submitScore = async () => {
                const metadata = {
                    mode: settings.name,
                    correctAnswers: score,
                    totalQuestions: settings.P,
                    accuracy: `${((score / settings.P) * 100).toFixed(0)}%`,
                    ...settings
                };
                try {
                    await apiClient.post('/scores', {
                        gameName: 'Numsense',
                        score: calculatedScore,
                        metadata: metadata
                    });
                    setIsScoreSubmitted(true);
                } catch (error) {
                    console.error('Failed to submit score:', error);
                }
            };
            
            if (calculatedScore > 0) {
                 submitScore();
            }
        }
        
        if (gameState === 'GAME_OVER' && !user) {
            const calculatedScore = calculateFinalScore(score, settings);
            setFinalScore(calculatedScore);
        }
 
    }, [gameState, user, isScoreSubmitted, score, settings, calculateFinalScore]);

    const startNewRound = useCallback(() => {
        if (!settings) return;

        setFeedback(null);
        setQuestionNumber(prev => prev + 1);

        const numDots = getRandomInt(settings.x1, settings.x2);
        setCorrectAnswer(numDots);

        // 生成点的位置
        const newDots = Array.from({ length: numDots }, () => ({
            x: getRandomInt(10, 90), // Keep away from edges
            y: getRandomInt(10, 90),
            size: 15,
        }));
        setDots(newDots);
        
        setGameState('SHOWING_DOTS');

        setTimeout(() => {
            setDots([]);
            setOptions(generateOptions(numDots, settings.x1, settings.x2));
            setGameState('ASKING_QUESTION');
        }, settings.n * 1000);

    }, [settings]);

    useEffect(() => {
        if (settings) {
            startNewRound();
        }
    }, [settings, startNewRound]);

    const handleModeSelect = (modeKey) => {
        setSettings(GAME_MODES[modeKey]);
    };
    
    const handleCustomSubmit = (e) => {
        e.preventDefault();
        setSettings({
            name: 'CUSTOM PROTOCOL',
            x1: Number(customSettings.x1),
            x2: Number(customSettings.x2),
            n: Number(customSettings.n),
            P: Number(customSettings.P),
        });
    };

    const handleAnswer = (answer) => {
        const isCorrect = answer === correctAnswer;
        
        if (isCorrect) {
            setScore(prev => prev + 1);
            setFeedback('correct');
        } else {
            setFeedback('incorrect');
        }
        // setHistory(prev => [...prev, isCorrect]);

        setTimeout(() => {
            if (questionNumber >= settings.P) {
                setGameState('GAME_OVER');
            } else {
                startNewRound();
            }
        }, 1000);
    };

    const resetGame = () => {
        setGameState('MODE_SELECTION');
        setSettings(null);
        setDots([]);
        setCorrectAnswer(0);
        setOptions([]);
        setFeedback(null);
        setQuestionNumber(0);
        setScore(0);
        setFinalScore(0);
        setIsScoreSubmitted(false);
        // setHistory([]);
    };

    // --- Render Logic ---

    const renderContent = () => {
        switch (gameState) {
            case 'MODE_SELECTION':
                return (
                    <CenteredContainer>
                        <Title>数覚</Title>
                        <Subtitle>一瞬之间，你能看清多少</Subtitle>
                        <OptionsWrapper>
                            {Object.keys(GAME_MODES).map(key => (
                                <ModeButton key={key} type="button" onClick={() => handleModeSelect(key)}>
                                    {modeLabel(GAME_MODES[key].name)}
                                    <span className="en">{GAME_MODES[key].name}</span>
                                </ModeButton>
                            ))}
                        </OptionsWrapper>
                        <CustomForm onSubmit={handleCustomSubmit}>
                            <FormHeader>自定义</FormHeader>
                            <FormLabel htmlFor="x1">最少点数</FormLabel>
                            <FormInput id="x1" type="number" value={customSettings.x1} onChange={e => setCustomSettings({...customSettings, x1: e.target.value})} min="1" />
                            <FormLabel htmlFor="x2">最多点数</FormLabel>
                            <FormInput id="x2" type="number" value={customSettings.x2} onChange={e => setCustomSettings({...customSettings, x2: e.target.value})} min={customSettings.x1} />
                            <FormLabel htmlFor="n">显示时长（秒）</FormLabel>
                            <FormInput id="n" type="number" value={customSettings.n} onChange={e => setCustomSettings({...customSettings, n: e.target.value})} step="0.1" min="0.1" />
                            <FormLabel htmlFor="P">题目数量</FormLabel>
                            <FormInput id="P" type="number" value={customSettings.P} onChange={e => setCustomSettings({...customSettings, P: e.target.value})} min="1" />
                            <PrimaryButton type="submit" $block style={{ gridColumn: '1 / -1', marginTop: '10px' }}>开始</PrimaryButton>
                        </CustomForm>
                    </CenteredContainer>
                );

            case 'SHOWING_DOTS':
                return null;

            case 'ASKING_QUESTION':
                return (
                    <CenteredContainer>
                         <Title $small>几个点？</Title>
                         <Subtitle>选择你看到的数量</Subtitle>
                         <OptionsWrapper>
                            {options.map((opt, index) => (
                                <AnswerButton key={index} type="button" onClick={() => handleAnswer(opt)} disabled={feedback !== null}>
                                    {opt}
                                </AnswerButton>
                            ))}
                        </OptionsWrapper>
                    </CenteredContainer>
                );

            case 'GAME_OVER':
                return (
                    <CenteredContainer>
                        <Title>終局</Title>
                        <Subtitle>{modeLabel(settings.name)}</Subtitle>
                        <StatsGrid>
                            <StatItem $wide $accent>
                                <StatLabel>最终得分</StatLabel>
                                <FinalScore>{finalScore.toLocaleString()}</FinalScore>
                            </StatItem>
                            <StatItem>
                                <StatLabel>答对</StatLabel>
                                <StatValue>{score}</StatValue>
                            </StatItem>
                            <StatItem>
                                <StatLabel>正确率</StatLabel>
                                <StatValue>{((score / settings.P) * 100).toFixed(0)}%</StatValue>
                            </StatItem>
                        </StatsGrid>

                        {user && isScoreSubmitted && (
                            <Note role="status">成绩已记入排行榜。</Note>
                        )}
                        {!user && (
                            <Note $dim>登录后成绩才会记入排行榜。</Note>
                        )}
                        <GhostButton type="button" onClick={resetGame} style={{ marginTop: '20px' }}>返回选单</GhostButton>
                    </CenteredContainer>
                );

            default:
                return null;
        }
    };

    return (
        <GameWrapper $feedback={feedback}>
            <LeaderboardButton
              type="button"
              onClick={() => setShowLeaderboard(true)}
              $visible={gameState === 'MODE_SELECTION'}
            >
                <FaTrophy aria-hidden="true" />
                排行榜
            </LeaderboardButton>

            {showLeaderboard && <Leaderboard gameName="Numsense" onClose={() => setShowLeaderboard(false)} />}

            {gameState !== 'MODE_SELECTION' && (
                <GameHeader>
                    <AbortButton type="button" onClick={resetGame}>中止</AbortButton>
                    <GameStats>
                        <span className="mode">{modeLabel(settings?.name)}</span>
                        <span>题目<b>{questionNumber} / {settings?.P}</b></span>
                        <span>得分<b>{score}</b></span>
                        <FeedbackText $feedback={feedback} aria-live="polite">
                            {feedback === 'correct' && '正确'}
                            {feedback === 'incorrect' && '错误'}
                        </FeedbackText>
                    </GameStats>
                </GameHeader>
            )}
            <GameCanvas>
                {gameState === 'SHOWING_DOTS' && dots.map((dot, i) => <Dot key={i} {...dot} />)}
                {renderContent()}
            </GameCanvas>
        </GameWrapper>
    );
}

function NumsensePage() {
    const { user, loading } = useAuth();

    return (
        <PageShell num="参" title="数覚" en="NUMSENSE">
            {loading ? (
                <Muted>载入中…</Muted>
            ) : (
                <NumsenseGame user={user} />
            )}
        </PageShell>
    );
}

export default NumsensePage;
